/**
 * sf-ros.js
 * Rain or Shine Golf — Shared Product Page JavaScript
 * Namespace: sfRos
 *
 * Handles all shared interactions for PDP description box components:
 * - Tab bar switching (Description, Specifications, Compare, Shipping, Why ROS, Reviews)
 * - FAQ accordion
 * - Sticky tab bar on scroll
 *
 * Loaded on all product pages via theme.liquid.
 * Description box HTML uses matching sf-ros-* classes and data attributes.
 * Zero dependencies — vanilla JS only.
 */

(function () {
  'use strict';

  /* ============================================================
     TAB BAR
     Looks for .sf-ros-tabs wrapper containing:
     - .sf-ros-tabs__nav  — the tab button container
     - .sf-ros-tabs__btn  — individual tab buttons (data-tab="id")
     - .sf-ros-tabs__panel — individual panels (id matching data-tab)
  ============================================================ */

  function sfRosInitTabs(wrapper) {
    var buttons = wrapper.querySelectorAll('.sf-ros-tabs__btn');
    var panels = wrapper.querySelectorAll('.sf-ros-tabs__panel');

    if (!buttons.length || !panels.length) return;

    function activateTab(targetId) {
      buttons.forEach(function (btn) {
        var isActive = btn.getAttribute('data-tab') === targetId;
        btn.classList.toggle('is-active', isActive);
        btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });

      panels.forEach(function (panel) {
        var isActive = panel.id === targetId;
        panel.classList.toggle('is-active', isActive);
        panel.setAttribute('hidden', isActive ? null : '');
        if (isActive) {
          panel.removeAttribute('hidden');
        } else {
          panel.setAttribute('hidden', '');
        }
      });
    }

    buttons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var targetId = btn.getAttribute('data-tab');
        if (targetId) activateTab(targetId);
      });
    });

    /* Activate first visible tab on init */
    var firstBtn = wrapper.querySelector('.sf-ros-tabs__btn');
    if (firstBtn) {
      activateTab(firstBtn.getAttribute('data-tab'));
    }
  }

  /* ============================================================
     STICKY TAB BAR
     Adds .is-sticky to .sf-ros-tabs__nav when user scrolls
     past the top of the tabs wrapper.
  ============================================================ */

  function sfRosInitStickyTabs(wrapper) {
    var nav = wrapper.querySelector('.sf-ros-tabs__nav');
    if (!nav) return;

    var ticking = false;

    function onScroll() {
      if (!ticking) {
        window.requestAnimationFrame(function () {
          var wrapperTop = wrapper.getBoundingClientRect().top;
          nav.classList.toggle('is-sticky', wrapperTop < 0);
          ticking = false;
        });
        ticking = true;
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ============================================================
     MOBILE TAB DROPDOWN
     On mobile, .sf-ros-tabs__nav collapses to a dropdown.
     Tapping .sf-ros-tabs__mobile-toggle opens/closes the menu.
  ============================================================ */

  function sfRosInitMobileTabToggle(wrapper) {
    var toggle = wrapper.querySelector('.sf-ros-tabs__mobile-toggle');
    var nav = wrapper.querySelector('.sf-ros-tabs__nav');
    if (!toggle || !nav) return;

    toggle.addEventListener('click', function () {
      var isOpen = nav.classList.toggle('is-mobile-open');
      toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    });

    /* Close dropdown when a tab is selected on mobile */
    wrapper.querySelectorAll('.sf-ros-tabs__btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        nav.classList.remove('is-mobile-open');
        toggle.setAttribute('aria-expanded', 'false');

        /* Update mobile toggle label to current tab */
        var label = btn.textContent.trim();
        var labelEl = toggle.querySelector('.sf-ros-tabs__mobile-label');
        if (labelEl) labelEl.textContent = label;
      });
    });
  }

  /* ============================================================
     FAQ ACCORDION
     Looks for .sf-ros-faq__item containing:
     - .sf-ros-faq__question — the clickable trigger button
     - .sf-ros-faq__answer   — the collapsible answer panel
     One item open at a time — opening one closes others.
  ============================================================ */

  function sfRosInitFaq(container) {
    var items = container.querySelectorAll('.sf-ros-faq__item');
    if (!items.length) return;

    function closeItem(item) {
      var answer = item.querySelector('.sf-ros-faq__answer');
      var question = item.querySelector('.sf-ros-faq__question');
      item.classList.remove('is-open');
      if (answer) answer.setAttribute('hidden', '');
      if (question) question.setAttribute('aria-expanded', 'false');
    }

    function openItem(item) {
      var answer = item.querySelector('.sf-ros-faq__answer');
      var question = item.querySelector('.sf-ros-faq__question');
      item.classList.add('is-open');
      if (answer) answer.removeAttribute('hidden');
      if (question) question.setAttribute('aria-expanded', 'true');
    }

    items.forEach(function (item) {
      var question = item.querySelector('.sf-ros-faq__question');
      var answer = item.querySelector('.sf-ros-faq__answer');

      /* Set initial ARIA state */
      if (answer) answer.setAttribute('hidden', '');
      if (question) {
        question.setAttribute('aria-expanded', 'false');
        question.setAttribute('role', 'button');
      }

      if (question) {
        question.addEventListener('click', function () {
          var isOpen = item.classList.contains('is-open');

          /* Close all items */
          items.forEach(closeItem);

          /* If it wasn't open, open it */
          if (!isOpen) openItem(item);
        });
      }
    });
  }

  /* ============================================================
     INIT — runs on DOMContentLoaded
     Finds all sf-ros components and initialises them.
  ============================================================ */

  function sfRosInit() {
    /* Tabs */
    var tabWrappers = document.querySelectorAll('.sf-ros-tabs');
    tabWrappers.forEach(function (wrapper) {
      sfRosInitTabs(wrapper);
      sfRosInitStickyTabs(wrapper);
      sfRosInitMobileTabToggle(wrapper);
    });

    /* FAQ accordions */
    var faqContainers = document.querySelectorAll('.sf-ros-faq');
    faqContainers.forEach(sfRosInitFaq);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sfRosInit);
  } else {
    sfRosInit();
  }

})();

/* ============================================================
   SF-ROS UPSELLS — Add-On Card Selection + Variant Modal + ATC Hook
   Append to sf-ros.js (replace previous upsells block)
   ============================================================ */

(function () {

  var upsellsEl = document.querySelector('[data-upsells]');
  if (!upsellsEl) return;

  var cards          = upsellsEl.querySelectorAll('[data-upsell-card]');
  var summaryEl      = upsellsEl.querySelector('[data-upsell-summary]');
  var summaryLinesEl = upsellsEl.querySelector('[data-upsell-summary-lines]');
  var summaryTotalEl = upsellsEl.querySelector('[data-upsell-total]');
  var upsellAtcBtn   = upsellsEl.querySelector('[data-upsell-atc]');
  var mainAtcBtn     = document.querySelector('[name="add"]');
  var productForm    = document.querySelector('form[action="/cart/add"]');

  /* Base price in cents — read from data attribute on upsell ATC button */
  var basePrice = upsellAtcBtn ? parseInt(upsellAtcBtn.dataset.basePrice || 0, 10) : 0;

  /* Why-we-picked-these modal elements */
  var ctaTrigger = upsellsEl.querySelector('[data-upsell-modal-trigger]');
  var whyModal   = document.querySelector('[data-upsell-modal]');
  var whyClose   = whyModal ? whyModal.querySelector('[data-upsell-modal-close]') : null;

  /* State */
  var selectedVariants   = [];
  var activeVariantCard  = null;
  var activeVariantModal = null;

  /* -------------------------------------------------------
     CARD INTERACTION
  ------------------------------------------------------- */

  cards.forEach(function (card) {
    card.addEventListener('click', function () {
      handleCardClick(card);
    });
    card.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleCardClick(card);
      }
    });
  });

  function handleCardClick(card) {
    var isSelected = card.classList.contains('is-selected');
    var isMulti    = card.dataset.multiVariant === 'true';

    if (isSelected) {
      /* Always deselect on re-click regardless of variant type */
      deselectCard(card);
    } else if (isMulti) {
      /* Not yet selected and multi-variant — open picker */
      openVariantModal(card);
    } else {
      /* Not yet selected and single-variant — toggle on */
      selectCard(card);
    }
  }

  function selectCard(card) {
    var variantId = card.dataset.variantId;
    card.classList.add('is-selected');
    card.setAttribute('aria-checked', 'true');
    if (selectedVariants.indexOf(variantId) === -1) {
      selectedVariants.push(variantId);
    }
    updateSummary();
  }

  function deselectCard(card) {
    var variantId = card.dataset.variantId;
    card.classList.remove('is-selected');
    card.setAttribute('aria-checked', 'false');

    /* Clear variant chosen label if multi-variant */
    var chosenEl = card.querySelector('.sf-ros-upsells__card-variant-chosen');
    if (chosenEl) chosenEl.textContent = '';

    selectedVariants = selectedVariants.filter(function (id) {
      return id !== variantId;
    });
    updateSummary();
  }

  /* -------------------------------------------------------
     VARIANT SELECTION MODAL
  ------------------------------------------------------- */

  function openVariantModal(card) {
    var handle = card.dataset.productHandle;
    var modal  = document.querySelector('[data-variant-modal][data-for-handle="' + handle + '"]');
    if (!modal) return;

    activeVariantCard  = card;
    activeVariantModal = modal;

  document.body.appendChild(modal);
modal.style.display = 'flex';
document.body.classList.add('sf-ros-upsells--modal-open');

    var select     = modal.querySelector('[data-variant-select]');
    var priceEl    = modal.querySelector('[data-variant-modal-price]');
    var confirmBtn = modal.querySelector('[data-variant-modal-confirm]');
    var closeBtn   = modal.querySelector('[data-variant-modal-close]');
    var qtyInput   = modal.querySelector('[data-modal-qty-input]');
    var qtyDec     = modal.querySelector('[data-modal-qty-decrement]');
    var qtyInc     = modal.querySelector('[data-modal-qty-increment]');

    /* Reset qty to 1 each time the modal opens (or carry over saved qty if already chosen) */
    if (qtyInput) {
      var savedQty = parseInt(card.dataset.qty, 10);
      qtyInput.value = (savedQty && savedQty >= 1) ? savedQty : 1;
    }

    /* Sync price on open with selected option AND current qty */
    syncModalPrice(select, priceEl, qtyInput);

    /* Update displayed price when variant changes */
    select.onchange = function () {
      syncModalPrice(select, priceEl, qtyInput);
    };

    /* Qty stepper handlers */
    if (qtyInput) {
      qtyInput.oninput = function () {
        var v = parseInt(qtyInput.value, 10);
        if (isNaN(v) || v < 1) qtyInput.value = 1;
        if (v > 999) qtyInput.value = 999;
        syncModalPrice(select, priceEl, qtyInput);
      };
      qtyInput.onchange = function () {
        syncModalPrice(select, priceEl, qtyInput);
      };
    }
    if (qtyDec) {
      qtyDec.onclick = function () {
        var v = parseInt(qtyInput.value, 10) || 1;
        if (v > 1) qtyInput.value = v - 1;
        syncModalPrice(select, priceEl, qtyInput);
      };
    }
    if (qtyInc) {
      qtyInc.onclick = function () {
        var v = parseInt(qtyInput.value, 10) || 1;
        qtyInput.value = Math.min(v + 1, 999);
        syncModalPrice(select, priceEl, qtyInput);
      };
    }

    /* Confirm. Lock in the chosen variant + qty */
    confirmBtn.onclick = function () {
      var opt            = select.options[select.selectedIndex];
      var variantId      = opt.value;
      var priceRaw       = parseInt(opt.dataset.price, 10);
      var priceFormatted = opt.dataset.priceFormatted;
      var variantLabel   = opt.dataset.label;
      var qty            = qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1;

      /* Update card data attributes to reflect chosen variant + qty */
      card.dataset.variantId = variantId;
      card.dataset.priceRaw  = priceRaw;
      card.dataset.price     = priceFormatted;
      card.dataset.qty       = qty;

      /* Mark card as selected */
      card.classList.add('is-selected');
      card.setAttribute('aria-checked', 'true');

      /* Show chosen variant label + qty on card */
      var chosenEl = card.querySelector('.sf-ros-upsells__card-variant-chosen');
      if (chosenEl) {
        var qtyPrefix = qty > 1 ? qty + 'x · ' : '';
        chosenEl.textContent = qtyPrefix + variantLabel + ' · ' + priceFormatted;
      }

      /* Replace any previous variant selection for this card */
      selectedVariants = selectedVariants.filter(function (id) {
        return id !== variantId;
      });
      selectedVariants.push(variantId);

      closeVariantModal();
      updateSummary();
    };

    /* Close button */
    if (closeBtn) {
      closeBtn.onclick = closeVariantModal;
    }

    /* Overlay click */
    modal.onclick = function (e) {
      if (e.target === modal) closeVariantModal();
    };
  }

  function syncModalPrice(select, priceEl, qtyInput) {
    if (!select || !priceEl) return;
    var opt = select.options[select.selectedIndex];
    if (!opt) return;
    var raw = parseInt(opt.dataset.price, 10);
    var qty = qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1;
    priceEl.textContent = formatMoney(raw * qty);
  }

  function closeVariantModal() {
    if (activeVariantModal) {
      activeVariantModal.style.display = 'none';
      activeVariantModal = null;
    }
    document.body.classList.remove('sf-ros-upsells--modal-open');
    if (activeVariantCard) {
      activeVariantCard.focus();
      activeVariantCard = null;
    }
  }

  /* -------------------------------------------------------
     WHY-WE-PICKED-THESE MODAL
  ------------------------------------------------------- */

  if (ctaTrigger && whyModal) {
    ctaTrigger.addEventListener('click', function () {
      document.body.appendChild(whyModal);
      whyModal.style.display = 'flex';
      document.body.classList.add('sf-ros-upsells--modal-open');
      if (whyClose) whyClose.focus();
    });
  }

  if (whyClose) {
    whyClose.addEventListener('click', closeWhyModal);
  }

  if (whyModal) {
    whyModal.addEventListener('click', function (e) {
      if (e.target === whyModal) closeWhyModal();
    });
  }

  function closeWhyModal() {
    if (!whyModal) return;
    whyModal.style.display = 'none';
    document.body.classList.remove('sf-ros-upsells--modal-open');
    if (ctaTrigger) ctaTrigger.focus();
  }

  /* -------------------------------------------------------
     ESC KEY — closes whichever modal is open
  ------------------------------------------------------- */

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    if (activeVariantModal && activeVariantModal.style.display === 'flex') {
      closeVariantModal();
    } else if (whyModal && whyModal.style.display === 'flex') {
      closeWhyModal();
    }
  });

  /* -------------------------------------------------------
     ORDER SUMMARY — main product + addons
  ------------------------------------------------------- */

  function updateSummary() {
    if (selectedVariants.length === 0) {
      summaryEl.style.display = 'none';
      updateAtcLabels();
      return;
    }

    summaryEl.style.display = 'block';

    var lines      = '';
    var addonTotal = 0;

    cards.forEach(function (card) {
      if (card.classList.contains('is-selected')) {
        var title    = card.dataset.productTitle;
        var priceRaw = parseInt(card.dataset.priceRaw || 0, 10);
        var qty      = parseInt(card.dataset.qty || 1, 10);
        var lineTotal = priceRaw * qty;
        var chosenEl = card.querySelector('.sf-ros-upsells__card-variant-chosen');
        var rawChosen = chosenEl && chosenEl.textContent ? chosenEl.textContent.split(' · ')[0] : '';
        // Strip leading qty prefix from chosen text since we will show qty in the line label below
        rawChosen = rawChosen.replace(/^\d+x\s*$/, '').trim();
        var variantText = rawChosen ? ' (' + rawChosen + ')' : '';
        var qtyPrefix   = qty > 1 ? qty + ' × ' : '';

        addonTotal += lineTotal;

        lines += '<div class="sf-ros-upsells__summary-addon-line">'
               + '<span>' + qtyPrefix + title + variantText + '</span>'
               + '<span>+' + formatMoney(lineTotal) + '</span>'
               + '</div>';
      }
    });

    summaryLinesEl.innerHTML = lines;
    summaryTotalEl.textContent = formatMoney(basePrice + addonTotal);
    updateAtcLabels();
  }

  /* -------------------------------------------------------
     MONEY FORMATTER — always includes cents
  ------------------------------------------------------- */

  function formatMoney(cents) {
    var amount = cents / 100;
    return '$' + amount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  /* -------------------------------------------------------
     ATC BUTTON LABELS — mirrors on both upsell and main button
  ------------------------------------------------------- */

  function updateAtcLabels() {
    var count = selectedVariants.length;
    var label = count === 0
      ? 'Add to Cart'
      : 'Add ' + (count + 1) + ' Items to Cart';

    if (upsellAtcBtn) upsellAtcBtn.textContent = label;
    if (mainAtcBtn) {
      mainAtcBtn.value       = label;
      mainAtcBtn.textContent = label;
    }
  }

  if (upsellAtcBtn) {
    upsellAtcBtn.addEventListener('click', function () {
      submitCart();
    });
  }

  /* -------------------------------------------------------
     CART SUBMIT — add selected variants then submit main form
  ------------------------------------------------------- */

  function submitCart() {
    if (!productForm) return;

    if (selectedVariants.length === 0) {
      productForm.submit();
      return;
    }

    var items = [];
    cards.forEach(function (card) {
      if (card.classList.contains('is-selected')) {
        var vid = parseInt(card.dataset.variantId, 10);
        var qty = parseInt(card.dataset.qty || 1, 10);
        if (vid) items.push({ id: vid, quantity: qty });
      }
    });

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items })
    })
    .then(function () {
      productForm.submit();
    })
    .catch(function (err) {
      console.error('ROS Upsells: add-on cart error', err);
      productForm.submit();
    });
  }

  /* Also hook the main product form submit so add-ons ride along
     if customer uses the main ATC button instead */
  if (productForm) {
    productForm.addEventListener('submit', function (e) {
      if (selectedVariants.length === 0) return;
      e.preventDefault();
      submitCart();
    });
  }
})();